import {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { setKeyValue, updateKeyValue } from 'modules/keyValueStore/actions';
import { useKeyValueStore } from 'modules/keyValueStore/useKeyValueStore';
import { FAILURE, PENDING, SUCCESS } from 'utils/constants/queryStatuses';
import { getTotalArticles } from './api';
import { arrangeArticles, LABELS_DATA } from './utils';

const ARTICLES_STORE_KEY = 'articles';
const DEFAULT_QUANTITY = 4;
const INCREMENT = 4;

const fetchArticles = (condition) => async (dispatch, getState) => {
  try {
    dispatch(updateKeyValue(ARTICLES_STORE_KEY, { status: PENDING, data: { condition } }));
    const { keyValueStore: { [ARTICLES_STORE_KEY]: { data: { condition: targetCondition } = {} } = {} } } = getState();
    if (condition !== targetCondition) {
      // handle race condition
      return;
    }

    const articles = await getTotalArticles(condition);
    dispatch(setKeyValue(
      ARTICLES_STORE_KEY,
      { status: SUCCESS, data: { condition, articles: arrangeArticles(articles) } },
    ));
  } catch {
    dispatch(setKeyValue(ARTICLES_STORE_KEY, { status: FAILURE }));
  }
};

export const useFetchArticles = (condition, initQuantity) => {
  const dispatch = useDispatch();
  const [
    { status: articlesFetchingStatus, data: { condition: storedArticlesCondition, articles: storedArticles } = {} } = {},
  ] = useKeyValueStore(ARTICLES_STORE_KEY);

  const [quantity, setQuantity] = useState(initQuantity ?? DEFAULT_QUANTITY);

  useEffect(() => {
    if (
      !condition
      || (storedArticlesCondition === condition && [PENDING, SUCCESS, FAILURE].includes(articlesFetchingStatus))
    ) {
      return;
    }
    dispatch(fetchArticles(condition));
  }, [storedArticlesCondition, articlesFetchingStatus, condition, dispatch]);

  const isLoaded = storedArticlesCondition === condition && articlesFetchingStatus === SUCCESS;

  const allArticles = isLoaded ? storedArticles : null;
  const articles = useMemo(() => (allArticles ?? []).slice(0, quantity), [quantity, allArticles]);

  return {
    articles,
    labels: LABELS_DATA,
    hasMore: isLoaded && articles.length < storedArticles.length,
    loadMoreArticles: useCallback((more = INCREMENT) => {
      setQuantity((prevQuantity) => prevQuantity + more);
    }, []),
    articlesFetchingStatus,
    isLoaded,
  };
};
